import { inject, Injectable } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';
import { RepairTaskItemDto, RepairTaskService } from '@artemis-software/wr-api';
import { openRepairTaskCloseDialog } from '@components/dialog/repair-task-close-dialog/repair-task-close-dialog.component';

type RepairTaskStatus = RepairTaskItemDto.StatusEnum;

export interface RepairTaskStatusTarget {
  id: string;
  title?: string;
}

export function isClosedRepairTaskStatus(status: RepairTaskStatus | null | undefined): boolean {
  return status === RepairTaskItemDto.StatusEnum.Finished || status === RepairTaskItemDto.StatusEnum.Declined;
}

@Injectable({ providedIn: 'root' })
export class RepairTaskStatusChangeService {
  private readonly repairTaskService = inject(RepairTaskService);
  private readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);

  async changeStatus(tasks: RepairTaskStatusTarget[], status: RepairTaskStatus): Promise<boolean> {
    if (tasks.length === 0) {
      return false;
    }
    try {
      if (isClosedRepairTaskStatus(status)) {
        const closed = await this.close(tasks, status);
        if (!closed) {
          return false;
        }
      } else {
        await firstValueFrom(this.repairTaskService.updateStatusForAll(status, tasks.map(task => task.id)));
      }
      this.toastrService.success('Status geändert', 'Reparaturauftrag');
      return true;
    } catch {
      return false;
    }
  }

  private async close(tasks: RepairTaskStatusTarget[], status: RepairTaskStatus): Promise<boolean> {
    const mode = status === RepairTaskItemDto.StatusEnum.Finished ? 'finish' : 'decline';
    const title = tasks.length === 1 ? tasks[0].title ?? '' : `${tasks.length} Reparaturaufträge`;
    const result = await openRepairTaskCloseDialog(this.dialogService, mode, title, false);
    if (!result) {
      return false;
    }
    if (tasks.length > 1) {
      await firstValueFrom(this.repairTaskService.updateStatusForAll(status, tasks.map(task => task.id), result.comment));
      return true;
    }
    const id = tasks[0].id;
    if (mode === 'finish') {
      await firstValueFrom(this.repairTaskService.finish(id, result));
    } else {
      await firstValueFrom(this.repairTaskService.decline(id, { comment: result.comment }));
    }
    return true;
  }
}
